import { useForm } from 'react-hook-form'
import { useMemo, useCallback, useEffect, useState } from 'react'
import { useEnterpriseAccountRequestVerificationEmail } from 'domains/public/api/auth/email-verification/enterpriseAccountRequestVerificationEmail.hooks'
import { useFormErrorMessageMap } from 'utils/react-hook-form/reactHookForm.hooks'
import { emailValidationPattern } from 'utils/validators/emailValidation'
import { type DashMessages } from 'intl/intl.types'
import { isNotFoundError, isInvalidCaptchaError } from 'api/utils/apollo/errors/isError.hooks'

type EmailVerificationFormData = {
  email: string
  reCaptchaToken: string
}

const defaultValues: EmailVerificationFormData = {
  email: '',
  reCaptchaToken: '',
}

export const usePublicRequestEmailVerificationForm = () => {
  const {
    requestVerificationEmail,
    apiSuccess: requestVerificationEmailSuccess,
    apiError: requestVerificationEmailError,
    apiLoading: requestVerificationEmailLoading,
  } = useEnterpriseAccountRequestVerificationEmail()

  const { register, handleSubmit, errors, setValue } = useForm<EmailVerificationFormData>({
    defaultValues,
  })

  const onSubmit = handleSubmit(({ email, reCaptchaToken }) => {
    requestVerificationEmail({
      email: email.trim(),
      reCaptchaToken,
    })
  })

  const emailFieldRef = register({ required: true, pattern: emailValidationPattern })
  const registerReCaptchaTokenField = useCallback(
    () => register({ name: 'reCaptchaToken' }, { required: true }),
    [register]
  )
  const setReCaptchaTokenValue = useCallback(
    (value: string, shouldValidate = false) => {
      setValue('reCaptchaToken', value, shouldValidate)
    },
    [setValue]
  )

  // If we get an error from the API, we want to reset our captcha
  const [reCaptchaResetHandler, setReCaptchaResetHandler] = useState<() => void>(null)

  useEffect(() => {
    if (requestVerificationEmailError && reCaptchaResetHandler) {
      setReCaptchaTokenValue('', false)
      reCaptchaResetHandler()
    }
  }, [setReCaptchaTokenValue, reCaptchaResetHandler, requestVerificationEmailError])

  const emailErrorMessageId = useFormErrorMessageMap({
    required: 'public.requestEmailVerification.form.fields.email.error.required',
    pattern: 'public.requestEmailVerification.form.fields.email.error.pattern',
  })(errors.email)

  const reCaptchaTokenErrorMessageId = useFormErrorMessageMap({
    required: 'public.logIn.form.fields.reCaptchaToken.error.required',
  })(errors.reCaptchaToken)

  const formErrorMessageId: DashMessages | undefined = useMemo(() => {
    if (!requestVerificationEmailError) return undefined

    if (isNotFoundError(requestVerificationEmailError)) {
      return 'public.requestEmailVerification.form.error.emailNotFound'
    }

    if (isInvalidCaptchaError(requestVerificationEmailError)) {
      return 'public.requestEmailVerification.form.error.captchaFailed'
    }

    return 'public.requestEmailVerification.form.error.common'
  }, [requestVerificationEmailError])

  return {
    requestVerificationEmailSuccess,
    disableFormFields: requestVerificationEmailLoading || requestVerificationEmailSuccess,
    onSubmit,
    emailFieldRef,
    setReCaptchaResetHandler,
    registerReCaptchaTokenField,
    setReCaptchaTokenValue,
    emailErrorMessageId,
    formErrorMessageId,
    reCaptchaTokenErrorMessageId,
    _setValue: setValue,
  }
}
